import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as pageService from '../../services/page';

export const fetchPages = createAsyncThunk(
    'pages/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await pageService.fetchPages();
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    },
    {
        condition: (_, { getState }) => getState().pages.status !== 'loading',
    }
);

export const fetchTemplates = createAsyncThunk('pages/fetchTemplates', async (_, { rejectWithValue }) => {
    try {
        return await pageService.fetchTemplates();
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const updatePage = createAsyncThunk('pages/update', async ({ id, changes }, { rejectWithValue }) => {
    try {
        return await pageService.updatePage(id, changes);
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

const initialState = {
    pages: [],
    // Grouped by slot folder: { pages: [], cards: [], articles: [] }.
    templates: { pages: [], cards: [], articles: [] },
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    saving: false,
    error: null,
};

// The API returns the updated page; swap it into the list in place so the grid
// keeps its order (nav_order) without a refetch.
const replacePage = (state, updated) => {
    const index = state.pages.findIndex((p) => p.idPage === updated.idPage);
    if (index >= 0) state.pages[index] = updated;
};

const pageSlice = createSlice({
    name: 'pages',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPages.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchPages.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.pages = action.payload;
            })
            .addCase(fetchPages.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(fetchTemplates.fulfilled, (state, action) => {
                state.templates = action.payload;
            })
            .addCase(updatePage.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(updatePage.fulfilled, (state, action) => {
                state.saving = false;
                replacePage(state, action.payload);
            })
            .addCase(updatePage.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            });
    },
});

export const { clearError } = pageSlice.actions;

export const selectAllPages = (state) => state.pages.pages;
export const selectTemplates = (state) => state.pages.templates;
export const selectPageStatus = (state) => state.pages.status;
export const selectPageSaving = (state) => state.pages.saving;
export const selectPageError = (state) => state.pages.error;

export default pageSlice.reducer;
